import { Component, EventEmitter, Input, OnDestroy, Output } from "@angular/core";
import { GroupAddressWithExtensions } from "@onsip/web/features/administrators/groups/groupsPage/groups-page.component";
import { FormControl, Validators } from "@angular/forms";
import { MemberType } from "@onsip/web/features/administrators/groups/selectMemberDragAndDrop/select-member-drag-drop.component";
import { ModalMaterialComponent } from "@onsip/web/features/shared/components/modal/modal-material.component";
import { take } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import { BehaviorSubject, Subscription } from "rxjs";
import { SelectMemberType } from "@onsip/web/features/administrators/groups/groupSetup/group-setup.component";
import { GroupAddressService } from "@onsip/common/services/api/resources/groupAddress/group-address.service";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { FullAddress } from "@onsip/common/services/api/apiResponse/address";

@Component({
  selector: "onsip-group-shelf-members-edit",
  templateUrl: "./group-shelf-members-edit.component.html",
  styleUrls: ["./group-shelf-members-edit.component.scss"],
  standalone: false
})
export class GroupShelfMembersEditComponent implements OnDestroy {
  constructor(
    private translate: TranslateService,
    private dialog: MatDialog,
    private snackbar: SnackbarService,
    private groupService: GroupAddressService
  ) {}
  @Output() editPanel = new EventEmitter<void>();
  group!: GroupAddressWithExtensions;
  E2eLocators = E2eLocators;
  loadingStateComplete$ = new BehaviorSubject(true);

  @Input() set groupInfo(groupInfo: GroupAddressWithExtensions) {
    this.group = groupInfo;
    this.setMembers(groupInfo);
  }

  memberType = new FormControl(MemberType.all, { nonNullable: true });
  memberTypes = Object.values(MemberType);
  maxItemCount = 10;

  searchMember = new FormControl("", { nonNullable: true });

  members = new FormControl(<Array<FullAddress>>[], {
    validators: [Validators.required, Validators.maxLength(10)],
    nonNullable: true
  });
  private unsubscriber = new Subscription();

  setMembers(group: GroupAddressWithExtensions): void {
    this.members.setValue(group.groupMemberAddresses.map(member => member.address));
  }

  selectMemberType(type: MemberType): SelectMemberType {
    return SelectMemberType[type];
  }

  onSubmit(): void {
    if (this.members.invalid) {
      return;
    }
    this.loadingStateComplete$.next(false);
    const SelectedAddresses = this.members.value
      .map(member => `${member.username}@${member.domain}`)
      .filter((member): member is string => !!member);

    const params = {
      Address: `${this.group.address.username}@${this.group.address.domain}`,
      DefaultAddress: `${this.group.defaultAddress?.username}@${this.group.defaultAddress?.domain}`,
      Name: this.group.address.name,
      Ordering: this.group.ordering,
      Timeout: this.group.timeout,
      Username: this.group.address.username,
      SelectedAddresses
    };

    if (this.group.defaultAddress) {
      params.DefaultAddress = `${this.group.defaultAddress?.username}@${this.group.defaultAddress?.domain}`;
    } else {
      params.DefaultAddress = "";
    }
    this.groupService.groupAddressEdit(params)?.then(res => {
      if (res && res.status === "success") {
        this.snackbar.openSnackBar(
          this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
          "success"
        );
        this.editPanel.emit();
      } else {
        this.snackbar.openSnackBar(res.data.message, "error");
        this.groupService.clearErrors();
      }
      this.loadingStateComplete$.next(true);
    });
  }

  cancel() {
    const modal = this.dialog.open(ModalMaterialComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"],
      data: {
        title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
        message: this.translate.instant("ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"),
        primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
        secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
        primaryBtnFlat: true
      }
    });

    this.unsubscriber.add(
      modal
        .afterClosed()
        .pipe(take(1))
        .subscribe(retObj => {
          if (retObj && retObj.doPrimaryAction) {
            this.editPanel.emit();
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }
}
